import React, { createContext, useContext, useState } from 'react';
import { translations } from '../locales/translations';

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = [
  { code: 'es', label: 'ES', flag: '🇨🇱', name: 'Español' },
  { code: 'pt', label: 'PT', flag: '🇧🇷', name: 'Português' },
  { code: 'en', label: 'EN', flag: '🇺🇸', name: 'English' }
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem('emigo_language');
      if (saved && ['es', 'pt', 'en'].includes(saved)) {
        return saved;
      }
    } catch (e) {
      console.warn("Could not read language from localStorage", e);
    }
    return 'es';
  });

  const setLanguage = (lang) => {
    if (['es', 'pt', 'en'].includes(lang)) {
      setLanguageState(lang);
      try {
        localStorage.setItem('emigo_language', lang);
      } catch (e) {
        console.warn("Could not save language to localStorage", e);
      }
    }
  };

  const t = (key, fallback = '') => {
    const langDict = translations[language] || translations['es'];
    if (langDict && langDict[key] !== undefined) {
      return langDict[key];
    }
    const esDict = translations['es'];
    if (esDict && esDict[key] !== undefined) {
      return esDict[key];
    }
    return fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, languages: SUPPORTED_LANGUAGES }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    // Retorno seguro con fallback si el componente se usa fuera del Provider
    return {
      language: 'es',
      setLanguage: () => {},
      t: (key, fallback = '') => {
        const esDict = translations['es'] || {};
        return esDict[key] !== undefined ? esDict[key] : (fallback || key);
      },
      languages: SUPPORTED_LANGUAGES
    };
  }
  return context;
};

// Componente visual ultra-compacto para cambiar de idioma
export const LanguageSelector = ({ variant = "pill", className = "" }) => {
  const { language, setLanguage, languages } = useLanguage();

  if (variant === "compact") {
    return (
      <div className={`d-flex align-items-center gap-1 ${className}`}>
        {languages.map((l) => (
          <button
            key={l.code}
            type="button"
            onClick={() => setLanguage(l.code)}
            className={`btn btn-sm py-0 px-1 border-0 rounded ${
              language === l.code
                ? 'bg-primary text-white fw-bold shadow-sm'
                : 'bg-transparent text-white-50 opacity-75'
            }`}
            style={{ fontSize: '11px', lineHeight: '18px' }}
            title={l.name}
          >
            {l.flag} {l.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div 
      className={`d-inline-flex align-items-center bg-dark bg-opacity-50 p-1 rounded-pill border border-secondary border-opacity-25 ${className}`}
      style={{ backdropFilter: 'blur(4px)' }}
    >
      {languages.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => setLanguage(l.code)}
          className={`btn btn-sm py-0 px-2 rounded-pill border-0 transition-all ${
            language === l.code
              ? 'bg-primary text-white fw-bold shadow-sm'
              : 'text-white-50 hover-text-white'
          }`}
          style={{ 
            fontSize: '11px', 
            height: '24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px'
          }}
          title={l.name}
        >
          <span style={{ fontSize: '12px' }}>{l.flag}</span>
          <span style={{ fontSize: '10px' }}>{l.label}</span>
        </button>
      ))}
    </div>
  );
};
